




























































import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  toRef,
  watch,
} from "@vue/composition-api";
import { cloneDeep } from "lodash-es";
import { LikeSubjectType } from "@scrile/api-provider/dist/api/LikeProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useContentFeed from "@/hooks/useContentFeed";
import providers from "@/lib/api";
import config from "@/config";
import { PROVIDED_DATA, PROVIDED_DATA_KEY } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import ContentFeedItem from "@/components/ContentFeedItem";
import InfiniteScroll from "@/components/InfiniteScroll";
import PageTitle from "@/components/PageTitle";
import PostTipping from "@/components/PostTipping";

export default defineComponent({
  name: "ContentFeedList",
  components: { BaseConfirm, BaseButton, PageTitle, InfiniteScroll, PostTipping },
  props: {
    scroll: { type: Boolean, required: true },
    enablePinPost: { type: Boolean, default: false },
    showFollowButton: { type: Boolean, default: false },
    showReportItem: { type: Boolean, default: true },
    showComment: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: true },
    showShare: { type: Boolean, default: true },
    showPrice: { type: Boolean, default: true },
    scrollToFeed: { type: String },
    contentFeedList: { type: Array as PropType<ContentFeed[]>, required: true },
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    followedUserIds: { type: Object as PropType<Record<string, boolean>>, default: () => ({}) },
  },
  setup(props, { emit, root }) {
    const { contentFeedDeletePost } = useContentFeed();

    const scrollToFeedIndex = ref(0);
    const showDeleteFeedConfirm = ref(false);
    const processingDelete = ref(false);
    const feedId = ref<string | null>(null);
    const pinnedPostIds = ref<string[]>([]);
    const feedIdToDelete = ref<string | null>(null);
    const observer = ref<IntersectionObserver | null>(null);
    const getItemView = ContentFeedItem;

    const providedData = ref<PROVIDED_DATA[PROVIDED_DATA_KEY.OPPONENT_USER]>({ user: null });

    const showTip = computed(() => {
      return providedData.value.user && config.featureTippingPosts;
    });

    const handleLike = useAuthorisedOnly(async (feedId: string) => {
      let likedFeed = cloneDeep(props.contentFeedList.find(i => i.id === feedId));
      if (!likedFeed) return;
      const likedResponse = await providers.LikeProvider.toggle({
        subject: { subjectId: feedId, subjectType: LikeSubjectType.CONTENT_SET },
      });
      likedFeed.likes.liked = likedResponse;
      if (likedResponse) {
        likedFeed.likes.count++;
      } else {
        likedFeed.likes.count = Math.max(0, likedFeed.likes.count - 1);
      }
      emit("update:contentFeed", likedFeed);
      emit("toggleLike", { feedId, status: likedResponse });
    });

    const handleTip = useAuthorisedOnly((item: ContentFeed) => {
      feedId.value = item.id;
      providedData.value.user = item.user;
    });

    const handleToggleFollow = useAuthorisedOnly(async (userId: string, cb: Function) => {
      try {
        const followStatus = await providers.FollowingsProvider.toggle({ id: userId });
        emit("toggleFollow", userId, followStatus);
      } finally {
        cb();
      }
    });

    const closeTip = () => {
      providedData.value.user = null;
    };

    const showDeleteFeed = (feedId: string) => {
      feedIdToDelete.value = feedId;
      showDeleteFeedConfirm.value = true;
    };

    const deleteFeed = async () => {
      if (!feedIdToDelete.value) return;
      try {
        processingDelete.value = true;
        if (await contentFeedDeletePost(feedIdToDelete.value)) {
          emit("delete", feedIdToDelete.value);
          showDeleteFeedConfirm.value = false;
        }
      } finally {
        processingDelete.value = false;
      }
    };

    const onChangeShowConfirm = (newState: boolean) => {
      if (!newState) feedIdToDelete.value = null;
    };

    const onTogglePin = (feedId: string, pinned: boolean) => {
      if (pinned) {
        pinnedPostIds.value.push(feedId);
        if (pinnedPostIds.value.length > config.maxAllowedPins) {
          pinnedPostIds.value.shift();
        }
        emit("update:contentFeed", {
          ...props.contentFeedList.find((feed: ContentFeed) => feed.id === feedId),
          pinTime: new Date().toUTCString(),
        });
      } else {
        pinnedPostIds.value = pinnedPostIds.value.filter(id => id !== feedId);
        emit("update:contentFeed", {
          ...props.contentFeedList.find((feed: ContentFeed) => feed.id === feedId),
          pinTime: null,
        });
      }
    };

    const scrollToFeedWatcher = async (feedIdNew?: string | null, feedIdOld?: string | null) => {
      if (!feedIdNew || !!feedIdOld) return;
      const selectedIndex = props.contentFeedList.findIndex(i => i.id === feedIdNew);
      scrollToFeedIndex.value = selectedIndex;
      if (selectedIndex > 0) {
        await nextTick();
        const el = root.$el.querySelector(`[data-id="${feedIdNew}"]`);
        el && el.scrollIntoView({ block: "start", behavior: "auto" });
      } else {
        window.scrollTo({ top: 0 });
      }
    };

    const orientationChangeHandler = () => {
      scrollToFeedWatcher(feedId.value);
    };

    const observerCb = (entries: IntersectionObserverEntry[]) => {
      let entry = entries.find(e => e.isIntersecting);
      if (!entry) return;
      const newFeedId = (entry.target as HTMLElement).dataset.id || null;
      feedId.value = newFeedId;
      emit("feedOnScreen", newFeedId);
    };

    const setObserve = (el: HTMLElement) => {
      if (!observer.value) {
        observer.value = new IntersectionObserver(entries => observerCb(entries), {
          rootMargin: "-40% 0px -60% 0px",
        });
      }
      observer.value?.observe(el);
    };

    watch(showDeleteFeedConfirm, onChangeShowConfirm);
    watch(toRef(props, "scrollToFeed"), (value, oldValue) => scrollToFeedWatcher(value, oldValue), { immediate: true });

    provide<PROVIDED_DATA[PROVIDED_DATA_KEY.OPPONENT_USER]>(PROVIDED_DATA_KEY.OPPONENT_USER, providedData.value);

    window.addEventListener("orientationchange", orientationChangeHandler);

    onMounted(() => {
      if (!props.enablePinPost) return;
      props.contentFeedList.forEach((item: ContentFeed) => {
        if (item.pinTime) pinnedPostIds.value = [...pinnedPostIds.value, item.id];
      });
    });

    onBeforeUnmount(() => {
      if (observer.value) observer.value.disconnect();
      window.removeEventListener("orientationchange", orientationChangeHandler);
    });
    return {
      feedId,
      showTip,
      processingDelete,
      scrollToFeedIndex,
      showDeleteFeedConfirm,
      handleTip,
      handleLike,
      handleToggleFollow,
      pinnedPostIds,
      onTogglePin,
      closeTip,
      setObserve,
      deleteFeed,
      getItemView,
      showDeleteFeed,
    };
  },
});
